// src/admin/AdminLayout.jsx
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton, ListItemIcon,
  ListItemText, IconButton, Divider, CssBaseline, Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase-config';
import { ADMIN_NAV } from './adminNav';
import Logo from '../components/Logo';

const DRAWER_WIDTH = 240;

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const current = ADMIN_NAV.find((n) => n.end ? location.pathname === n.path : location.pathname.startsWith(n.path));

  const drawer = (
    <Box>
      <Toolbar sx={{ gap: 1 }}>
        <Logo height={24} />
      </Toolbar>
      <Divider />
      <List>
        {ADMIN_NAV.map((item) => {
          const Icon = item.icon;
          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              end={item.end}
              sx={{ '&.active': { bgcolor: 'action.selected' } }}
            >
              <ListItemIcon><Icon /></ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{current?.label || 'Admin'}</Typography>
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>{currentUser?.email}</Typography>
            <Button color="inherit" startIcon={<HomeIcon />} onClick={() => navigate('/')} sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}>
              Home
            </Button>
            <Button color="inherit" startIcon={<SpaceDashboardIcon />} onClick={() => navigate('/dashboard')} sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}>
              Dashboard
            </Button>
            <IconButton color="inherit" onClick={() => navigate('/')} aria-label="Home" sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
              <HomeIcon />
            </IconButton>
            <IconButton color="inherit" onClick={() => { signOut(auth); navigate('/'); }} aria-label="Esci">
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent" open
            sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
          >
            {drawer}
          </Drawer>
        </Box>

        <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
          <Toolbar />
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
